<template>
  <!-- 图片查看器（带缩放功能） -->
  <ImageViewer
    v-model:show="showImageViewer"
    :src="previewBlobUrl"
    :alt="attachment?.filename || attachment?.name"
  />
  
  <div
    v-if="visible"
    @click="closePreview"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75"
  >
    <div
      @click.stop
      class="relative bg-white rounded-lg shadow-2xl"
      :class="getModalSizeClass()"
    >
      <!-- 头部工具栏 -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div class="flex items-center space-x-3">
          <component :is="getFileIcon()" class="h-8 w-8" :class="getFileIconColor()" />
          <div>
            <h3 class="text-lg font-medium text-gray-900">{{ attachment?.filename || attachment?.name }}</h3>
            <p class="text-sm text-gray-500">
              {{ formatFileSize(attachment?.size) }} • {{ getFileTypeName() }}
            </p>
          </div>
        </div>
        
        <div class="flex items-center space-x-2">
          <!-- 下载按钮 -->
          <button
            @click="downloadFile"
            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors"
            title="下载文件"
          >
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          
          <!-- 关闭按钮 -->
          <button
            @click="closePreview"
            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors"
            title="关闭预览"
          >
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      <!-- 预览内容区域 -->
      <div class="p-4 overflow-auto" :class="getContentAreaClass()">
        <!-- 统一的加载状态显示 -->
        <div v-if="loadingContent" class="flex items-center justify-center h-full min-h-[200px]">
          <div class="flex items-center space-x-3 text-gray-600">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span class="text-lg font-medium">加载中...</span>
          </div>
        </div>
        
        <!-- 统一的错误状态显示 -->
        <div v-else-if="contentError" class="flex items-center justify-center h-full min-h-[200px] text-red-500">
          <div class="text-center">
            <ExclamationTriangleIcon class="h-12 w-12 mx-auto mb-2" />
            <p class="text-lg">预览失败</p>
          </div>
        </div>
        
        <!-- 图片预览 -->
        <div v-else-if="isImage() && previewBlobUrl && !loadingContent && !contentError" class="flex justify-center items-center h-full">
          <img
            :src="previewBlobUrl"
            :alt="attachment?.filename || attachment?.name"
            class="max-w-full max-h-full object-contain rounded shadow-lg cursor-zoom-in hover:opacity-90 transition-opacity"
            @click="openImageViewer"
            @error="handleImageError"
            title="点击放大查看"
          />
        </div>
        
        <!-- PDF预览 -->
        <div v-else-if="isPdf() && previewBlobUrl && !loadingContent && !contentError" class="w-full h-full">
          <iframe
            :src="previewBlobUrl"
            class="w-full h-full border-0 rounded"
          ></iframe>
        </div>
        
        <!-- 视频预览 -->
        <div v-else-if="isVideo() && previewBlobUrl && !loadingContent && !contentError" class="flex justify-center items-center h-full">
          <video
            :src="previewBlobUrl"
            controls
            class="max-w-full max-h-full rounded shadow-lg"
            preload="metadata"
          >
            您的浏览器不支持视频播放
          </video>
        </div>
        
        <!-- 音频预览 -->
        <div v-else-if="isAudio() && previewBlobUrl && !loadingContent && !contentError" class="flex flex-col justify-center items-center h-full space-y-4">
          <div class="text-center">
            <component :is="getFileIcon()" class="h-20 w-20 mx-auto mb-4" :class="getFileIconColor()" />
            <h4 class="text-lg font-medium text-gray-900 mb-2">{{ attachment?.filename || attachment?.name }}</h4>
            <p class="text-gray-500 mb-4">{{ getFileTypeName() }} • {{ formatFileSize(attachment?.size) }}</p>
          </div>
          <audio
            :src="previewBlobUrl"
            controls
            class="w-full max-w-md"
            preload="metadata"
          >
            您的浏览器不支持音频播放
          </audio>
        </div>
        
        <!-- 文本文件预览 -->
        <div v-else-if="isText() && textContent && !loadingContent && !contentError" class="w-full">
          <div class="bg-gray-50 p-4 rounded-lg border">
            <pre class="whitespace-pre-wrap text-sm text-gray-800 font-mono">{{ textContent }}</pre>
          </div>
        </div>
        
        <!-- 不支持预览的文件类型或等待状态 -->
        <div v-else-if="!loadingContent && !contentError" class="text-center p-8">
          <component :is="getFileIcon()" class="mx-auto h-16 w-16 mb-4" :class="getFileIconColor()" />
          <h4 class="text-lg font-medium text-gray-900 mb-2">{{ getFileTypeName() }}</h4>
          <p class="text-gray-500 mb-4">此文件类型不支持预览</p>
          <button
            @click="downloadFile"
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center mx-auto"
          >
            <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            下载文件
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { 
  DocumentTextIcon, 
  PhotoIcon, 
  FilmIcon, 
  MusicalNoteIcon,
  ArchiveBoxIcon,
  CodeBracketIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import { getApiBaseUrlSync } from '@/services/configService'
import apiRequest from '@/utils/api'
import ImageViewer from '@/components/common/ImageViewer.vue'
import { isPublicObjectStorageUrl, toApiRelativePath } from '@/utils/attachmentUrl'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  attachment: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'download', 'urlResolved'])

// 响应式数据
const loadingContent = ref(false)
const contentError = ref(false)
const textContent = ref('')
const previewBlobUrl = ref('')  // 用于存储blob URL
const showImageViewer = ref(false)  // 图片查看器显示状态
const imageDirectFailed = ref(false)  // 直链失败后回退 blob

// 打开图片查看器
const openImageViewer = () => {
  if (previewBlobUrl.value) {
    showImageViewer.value = true
  }
}

// 获取文件类型
const getFileType = () => {
  if (!props.attachment?.content_type) return 'unknown'
  
  const contentType = props.attachment.content_type.toLowerCase()
  
  // 图片类型
  if (contentType.startsWith('image/')) return 'image'
  
  // PDF类型
  if (contentType === 'application/pdf') return 'pdf'
  
  // Office文档类型
  if (contentType.includes('msword') || 
      contentType.includes('wordprocessingml')) return 'word'
  
  if (contentType.includes('excel') || 
      contentType.includes('spreadsheetml')) return 'excel'
  
  if (contentType.includes('powerpoint') || 
      contentType.includes('presentationml')) return 'powerpoint'
  
  // 文本类型
  if (contentType.startsWith('text/')) return 'text'
  
  // 视频类型
  if (contentType.startsWith('video/')) return 'video'
  
  // 音频类型
  if (contentType.startsWith('audio/')) return 'audio'
  
  // 压缩文件
  if (contentType.includes('zip') || 
      contentType.includes('rar') || 
      contentType.includes('7z') ||
      contentType.includes('tar') ||
      contentType.includes('gz')) return 'archive'
  
  // 代码文件
  if (contentType.includes('javascript') ||
      contentType.includes('json') ||
      contentType.includes('xml') ||
      contentType.includes('html') ||
      contentType.includes('css')) return 'code'
  
  return 'unknown'
}

// 判断文件类型的便捷方法
const isImage = () => getFileType() === 'image'
const isPdf = () => getFileType() === 'pdf'
const isText = () => getFileType() === 'text'
const isVideo = () => getFileType() === 'video'
const isAudio = () => getFileType() === 'audio'

// 获取文件图标
const getFileIcon = () => {
  const fileType = getFileType()
  
  const iconMap = {
    image: PhotoIcon,
    video: FilmIcon,
    audio: MusicalNoteIcon,
    archive: ArchiveBoxIcon,
    code: CodeBracketIcon,
    pdf: DocumentTextIcon,
    word: DocumentTextIcon,
    excel: DocumentTextIcon,
    powerpoint: DocumentTextIcon,
    text: DocumentTextIcon,
    unknown: DocumentTextIcon
  }
  
  return iconMap[fileType] || DocumentTextIcon
}

// 获取文件图标颜色
const getFileIconColor = () => {
  const fileType = getFileType()
  
  const colorMap = {
    image: 'text-green-500',
    video: 'text-purple-500', 
    audio: 'text-yellow-500',
    archive: 'text-orange-500',
    code: 'text-blue-500',
    pdf: 'text-red-500',
    word: 'text-blue-600',
    excel: 'text-green-600',
    powerpoint: 'text-orange-600',
    text: 'text-gray-600',
    unknown: 'text-gray-500'
  }
  
  return colorMap[fileType] || 'text-gray-500'
}

// 获取文件类型显示名称
const getFileTypeName = () => {
  const fileType = getFileType()
  
  const nameMap = {
    image: '图片文件',
    video: '视频文件',
    audio: '音频文件', 
    archive: '压缩文件',
    code: '代码文件',
    pdf: 'PDF文档',
    word: 'Word文档',
    excel: 'Excel表格',
    powerpoint: 'PowerPoint演示文稿',
    text: '文本文件',
    unknown: '文件'
  }
  
  return nameMap[fileType] || '文件'
}

// 获取模态窗口大小类
const getModalSizeClass = () => {
  const fileType = getFileType()
  
  // 图片、视频、PDF 使用统一的固定大尺寸
  if (fileType === 'image' || fileType === 'video' || fileType === 'pdf') {
    return 'w-6xl h-5xl'
  }
  
  if (fileType === 'audio') return 'w-4xl max-h-3xl'
  if (fileType === 'text') return 'w-4xl max-h-4xl'
  
  return 'w-3xl max-h-3xl'
}

// 获取内容区域类
const getContentAreaClass = () => {
  const fileType = getFileType()
  
  // 图片、视频、PDF 使用固定的高度设置
  if (fileType === 'image' || fileType === 'video' || fileType === 'pdf') {
    return 'h-96 md:h-[500px]'
  }
  
  if (fileType === 'audio') return 'h-auto min-h-[200px]'
  if (fileType === 'text') return 'max-h-96 md:max-h-[500px]'
  
  return 'max-h-[70vh]'
}

// 获取API基础URL（从配置服务）
const getApiBaseUrl = () => {
  return getApiBaseUrlSync()
}

// 获取预览URL
const getPreviewUrl = () => {
  if (!props.attachment) return ''
  
  const previewPath = props.attachment.preview_url || props.attachment.download_url || ''
  if (isPublicObjectStorageUrl(previewPath)) {
    return previewPath
  }
  
  const apiBase = getApiBaseUrl()
  const downloadPath = toApiRelativePath(previewPath)
  return `${apiBase}${downloadPath}?preview=true`
}

// 获取PDF查看器URL  
const getPdfViewerUrl = () => {
  const pdfUrl = getPreviewUrl()
  return pdfUrl // 现代浏览器可以直接显示PDF
}

// 格式化文件大小
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B'
  
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
}

const getAttachmentApiPath = () => {
  if (!props.attachment) return ''
  if (props.attachment.id) {
    return `/api/emails/v2/attachments/${props.attachment.id}/download/`
  }
  return toApiRelativePath(props.attachment.download_url || props.attachment.preview_url || '')
}

/** 一次请求：补传 MinIO 并拿回直链 */
const resolveDirectUrl = async () => {
  const apiPath = getAttachmentApiPath()
  if (!apiPath) return null

  const existing = props.attachment.download_url || props.attachment.preview_url
  if (isPublicObjectStorageUrl(existing)) {
    return existing
  }

  const resp = await apiRequest.get(apiPath, { params: { resolve_url: 'true' } })
  const url = resp.data?.direct_url || resp.data?.download_url
  if (url && props.attachment?.id) {
    emit('urlResolved', { id: props.attachment.id, url })
  }
  return url || null
}

const loadPreviewViaBlob = async (apiPath) => {
  const response = await apiRequest.get(apiPath, {
    params: { preview: 'true' },
    responseType: 'blob'
  })
  previewBlobUrl.value = window.URL.createObjectURL(response.data)
}

// 图片优先 MinIO 直链（单次 resolve_url 请求即补传+返回）；失败则回退 blob 代理
const loadPreviewBlob = async () => {
  if (!props.attachment || isText()) return
  
  loadingContent.value = true
  contentError.value = false
  
  try {
    const apiPath = getAttachmentApiPath()

    if (isImage()) {
      const directUrl = await resolveDirectUrl()
      if (directUrl) {
        previewBlobUrl.value = directUrl
        return
      }
    }

    await loadPreviewViaBlob(apiPath)
  } catch (error) {
    console.error('加载预览内容失败:', error)
    contentError.value = true
  } finally {
    loadingContent.value = false
  }
}

// 直链图片加载失败时回退 API blob
const handleImageError = async () => {
  if (!isImage() || imageDirectFailed.value || loadingContent.value) return
  if (previewBlobUrl.value?.startsWith('blob:')) return
  imageDirectFailed.value = true
  try {
    loadingContent.value = true
    previewBlobUrl.value = ''
    await loadPreviewViaBlob(getAttachmentApiPath())
  } catch (error) {
    console.error('图片直链加载失败，回退代理也失败:', error)
    contentError.value = true
  } finally {
    loadingContent.value = false
  }
}

// 加载文本内容
const loadTextContent = async () => {
  if (!isText() || !props.attachment) return
  
  loadingContent.value = true
  contentError.value = false
  
  try {
    const apiPath = props.attachment.id
      ? `/api/emails/v2/attachments/${props.attachment.id}/download/`
      : toApiRelativePath(props.attachment.download_url || props.attachment.preview_url || '')
    const response = await apiRequest.get(apiPath, {
      params: { preview: 'true' },
      responseType: 'text'
    })
    
    textContent.value = response.data
  } catch (error) {
    console.error('加载文本内容失败:', error)
    contentError.value = true
  } finally {
    loadingContent.value = false
  }
}

// 处理内容加载成功
const handleContentLoad = () => {
  loadingContent.value = false
  contentError.value = false
}

// 处理内容加载失败
const handleContentError = () => {
  loadingContent.value = false
  contentError.value = true
}

// 关闭预览
const closePreview = () => {
  emit('close')
}

// 下载文件
const downloadFile = () => {
  emit('download', props.attachment)
}

// 清理blob URL
const cleanupBlobUrl = () => {
  if (previewBlobUrl.value && previewBlobUrl.value.startsWith('blob:')) {
    window.URL.revokeObjectURL(previewBlobUrl.value)
  }
  previewBlobUrl.value = ''
}

// 加载内容
const loadContent = async () => {
  cleanupBlobUrl()
  textContent.value = ''
  contentError.value = false
  loadingContent.value = false
  imageDirectFailed.value = false
  
  if (!props.attachment || !props.visible) return Promise.resolve()
  
  if (isText()) {
    return loadTextContent()
  } else if (isImage() || isPdf() || isVideo() || isAudio()) {
    return loadPreviewBlob()
  }
  
  return Promise.resolve()
}

// 监听器的组合逻辑
watch([() => props.attachment, () => props.visible], ([attachment, visible]) => {
  if (attachment && visible) {
    loadContent()
  } else {
    cleanupBlobUrl()
    textContent.value = ''
    contentError.value = false
    loadingContent.value = false
  }
}, { immediate: true })

// 组件卸载时清理
onUnmounted(() => {
  cleanupBlobUrl()
})
</script>
